import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { grantApi } from '../lib/api'
import type { GrantProfile, UpdateGrantProfileRequest } from '../types/grant'

export const GRANT_PROFILE_KEY = ['grant', 'profile'] as const
const GRANT_ME_KEY = ['grant', 'me'] as const

/** [Phase 7] Own name/phone/email + today's counts. Holds phone/email, so logout removes it (useGrantSession). */
export function useGrantProfile() {
  return useQuery({
    queryKey: GRANT_PROFILE_KEY,
    queryFn: async () => (await grantApi.profile()).data,
    retry: false,
    staleTime: 30_000,
  })
}

/** Returns the Promise so the mutation stays pending until the refetch lands. */
function useInvalidateProfile() {
  const qc = useQueryClient()
  return () => Promise.all([
    qc.invalidateQueries({ queryKey: GRANT_PROFILE_KEY }),
    qc.invalidateQueries({ queryKey: GRANT_ME_KEY }), // header avatar + name
  ])
}

/** The cached profile with a saved PATCH applied: names trimmed; phone/email null = unchanged, "" = cleared. */
export function applySavedProfile(old: GrantProfile, body: UpdateGrantProfileRequest): GrantProfile {
  const method = (sent: string | null | undefined, prev: string | null) =>
    sent == null ? prev : sent === '' ? null : sent
  return {
    ...old,
    firstName: body.firstName.trim(),
    lastName: body.lastName.trim(),
    phone: method(body.phone, old.phone),
    email: method(body.email, old.email),
  }
}

export function useUpdateGrantProfile() {
  const qc = useQueryClient()
  const invalidate = useInvalidateProfile()
  return useMutation({
    mutationFn: (body: UpdateGrantProfileRequest) => grantApi.updateProfile(body),
    // Write the saved values first so the form never reseeds from the pre-save cache (and keeps them if the
    // refetch fails); the refetch then brings the server's normalised phone/email.
    onSuccess: (_res, body) => {
      qc.setQueryData<GrantProfile>(GRANT_PROFILE_KEY, old => old && applySavedProfile(old, body))
      return invalidate()
    },
  })
}

export function useUploadGrantPortrait() {
  const invalidate = useInvalidateProfile()
  return useMutation({ mutationFn: (image: Blob) => grantApi.uploadPortrait(image), onSuccess: invalidate })
}

export function useDeleteGrantPortrait() {
  const invalidate = useInvalidateProfile()
  return useMutation({ mutationFn: () => grantApi.deletePortrait(), onSuccess: invalidate })
}
